/**
 * playSceneCollectibles.ts
 * Pure helper that populates `gameState.collectibles`, `gameState.weaponPickups`,
 * and the shard counters from parsed Tiled level data. Extracted from
 * `PlayScene.init()` so the scene file stays under the AGENTS.md 300-line
 * cap and so the `weaponPickups` field added in PR #111 wires in symmetrically
 * with the other collectible arrays.
 *
 * No React, no Skia, no async — pure data transform that runs once per level
 * load.
 *
 * @module playSceneCollectibles
 */

import { gameState, type CollectibleRuntime, type WeaponPickupRuntime } from '../engine/gameState';
import type {
  StarSeedData,
  ConstellationShardData,
  HeartFragmentData,
  WeaponPickupData,
} from '../types/level';

/**
 * Populate `gameState` with the collectible and weapon-pickup runtime data
 * parsed from the level's Tiled Collectibles layer.
 *
 * Mutates the singletons `gameState.collectibles`, `gameState.weaponPickups`,
 * `gameState.shardsTotal`, and `gameState.shardsCollected`. Pickup runtimes
 * differ from {@link WeaponPickupData} only by adding `collected: false`.
 *
 * @param starSeeds       - Star Seed positions from level data.
 * @param shards          - Constellation Shard positions from level data.
 * @param heartFragments  - Heart Fragment positions from level data.
 * @param weaponPickups   - Weapon pickup spawn data from level data.
 */
export function populateRuntimeCollectibles(
  starSeeds: StarSeedData[],
  shards: ConstellationShardData[],
  heartFragments: HeartFragmentData[],
  weaponPickups: WeaponPickupData[],
): void {
  const collectibles: CollectibleRuntime[] = [];
  for (const s of starSeeds) {
    collectibles.push({ id: s.id, kind: 'seed', x: s.x, y: s.y, collected: false });
  }
  for (const sh of shards) {
    collectibles.push({ id: sh.id, kind: 'shard', x: sh.x, y: sh.y, collected: false });
  }
  for (const hf of heartFragments) {
    collectibles.push({ id: hf.id, kind: 'heart_fragment', x: hf.x, y: hf.y, collected: false });
  }
  gameState.collectibles = collectibles;
  // Track shard totals for HUD display.
  gameState.shardsTotal = shards.length;
  gameState.shardsCollected = 0;

  // DECISION: WeaponPickupData → WeaponPickupRuntime adds `collected: false`
  // so the engine's pickup step can flip it without needing to mutate the
  // immutable level data.
  const pickups: WeaponPickupRuntime[] = [];
  for (const p of weaponPickups) {
    pickups.push({ id: p.id, kind: p.kind, x: p.x, y: p.y, collected: false });
  }
  gameState.weaponPickups = pickups;
}
